import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography,
} from '@mui/material';
import { ETYKIETA_ZAKRESU, type KolizjaZastosowania, type ZakresPaska } from '@/lib/api/tresci';
import { oknoWarszawa } from '@/lib/tresci/biblioteka';
import { etykietaOsoby, useOsobyPelne } from '@/lib/tresci/osobySkorek';
import type { WartoscOdbiorcow } from '@/lib/tresci/tresciUi';

/**
 * „ZASTĄPIĆ OBECNĄ SKÓRKĘ?” (OWNER 17.10) — potwierdzenie zamiany przy kolizji.
 *
 * Pokazuje WSZYSTKIE zastosowania, które zostaną wyłączone (pełny adresat: osoba = imię i nazwisko + pełny e-mail),
 * nową skórkę, jej adresatów, termin i priorytet. Wyłączenie dotyczy całego kolidującego zastosowania — także jego
 * adresatów spoza nowego wyboru — dlatego lista pokazuje każdego z nich.
 */

export interface PotwierdzZamianeProps {
  nowa: string;
  konflikty: KolizjaZastosowania[];
  zakres: ZakresPaska;
  odbiorcy: WartoscOdbiorcow;
  okno: string;
  priorytet: number;
  trwa: boolean;
  onAnuluj: () => void;
  onZamien: () => void;
}

/** Adresaci nowego zastosowania czytelnie: nazwy miast i szkół, osoby z pełnymi danymi (gdy są). */
export function adresaciNowej(zakres: ZakresPaska, o: WartoscOdbiorcow): string[] {
  if (zakres === 'GLOBAL') return [ETYKIETA_ZAKRESU.GLOBAL];
  if (zakres === 'CITY') return o.miasta.map((m) => m.name);
  if (zakres === 'SCHOOL') return o.szkoly.map((s) => s.name);
  return o.uczniowie.map((u) => (u.fullName || u.email
    ? `${(u.fullName ?? '').trim() || u.displayName} · ${u.email ?? u.maskedEmail ?? '—'}`
    : `${u.displayName} · ${u.maskedEmail ?? '—'}`));
}

export function PotwierdzZamiane({ nowa, konflikty, zakres, odbiorcy, okno, priorytet, trwa, onAnuluj, onZamien }: PotwierdzZamianeProps) {
  const osoby = useOsobyPelne(konflikty.filter((k) => k.scope === 'USER').flatMap((k) => k.targets.map((t) => t.id)));
  const obecne = [...new Set(konflikty.map((k) => k.name))];

  return (
    <Dialog open onClose={trwa ? undefined : onAnuluj} maxWidth="sm" fullWidth aria-labelledby="zamiana-tytul">
      <DialogTitle id="zamiana-tytul">Zastąpić obecną skórkę?</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="overline" sx={{ display: 'block' }}>Obecna</Typography>
            <Typography variant="body1" data-testid="zamiana-obecna"><b>{obecne.join(', ')}</b></Typography>
          </Box>
          <Box>
            <Typography variant="overline" sx={{ display: 'block' }}>Nowa</Typography>
            <Typography variant="body1" data-testid="zamiana-nowa"><b>{nowa}</b></Typography>
          </Box>
          <Box>
            <Typography variant="overline" sx={{ display: 'block' }}>Adresaci ({ETYKIETA_ZAKRESU[zakres]})</Typography>
            <Stack component="ul" spacing={0.25} sx={{ m: 0, pl: 2.5 }} data-testid="zamiana-adresaci">
              {adresaciNowej(zakres, odbiorcy).map((a) => (
                <Typography component="li" variant="body2" key={a} sx={{ wordBreak: 'break-word' }}>{a}</Typography>
              ))}
            </Stack>
          </Box>
          <Box>
            <Typography variant="overline" sx={{ display: 'block' }}>Termin</Typography>
            <Typography variant="body2" data-testid="zamiana-termin">{okno} · priorytet {priorytet}</Typography>
          </Box>
          <Box>
            <Typography variant="overline" sx={{ display: 'block' }}>
              {konflikty.length === 1 ? 'Zostanie wyłączone' : `Zostaną wyłączone (${konflikty.length})`}
            </Typography>
            <Stack component="ol" spacing={0.75} sx={{ m: 0, pl: 2.5 }} data-testid="zamiana-wylaczone">
              {konflikty.map((k) => (
                <Typography component="li" variant="body2" key={`${k.assignmentId}-${k.revisionId}`} sx={{ wordBreak: 'break-word' }}>
                  <b>{k.name}</b> — {ETYKIETA_ZAKRESU[k.scope]}
                  {k.scope === 'GLOBAL' ? null : `: ${k.targets.map((t) => (k.scope === 'USER' ? etykietaOsoby(t.id, t.name, osoby) : t.name)).join(', ')}`}
                  {' · '}{oknoWarszawa(k.startsAt, k.endsAt)} · priorytet {k.priority}
                </Typography>
              ))}
            </Stack>
          </Box>
          <Alert severity="warning" role="note">
            Obecne kolidujące zastosowanie zostanie wyłączone, a nowa skórka zacznie obowiązywać w wybranym zakresie.
            Wyłączenie obejmuje całe zastosowanie (wszystkich jego adresatów). Inne zastosowania zostają bez zmian,
            a historia zachowa obie zmiany.
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onAnuluj} disabled={trwa}>Anuluj</Button>
        <Button variant="contained" color="warning" onClick={onZamien} disabled={trwa}>{trwa ? 'Zamieniam…' : 'Zamień skórkę'}</Button>
      </DialogActions>
    </Dialog>
  );
}
